"use client";

import { useRef } from "react";
import { PageCareerSchema } from "@/sanity/pages/PageCareerTemplate";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { easeInOut, motion, useScroll, useTransform } from "framer-motion";
import bg from "@/public/global/bg-art.jpg";
import coinGuy from "@/public/aboutUs/heroImg.png";
import statueReading from "@/public/career/statueReading.png";

export default function HeroSection({
  data,
  className,
  showTopBanner,
  futures = false,
}: {
  data: PageCareerSchema;
  className?: string;
  showTopBanner?: boolean;
  futures?: boolean;
}) {
  const parallaxRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: parallaxRef,
    offset: ["start end", "end start"],
  });

  return (
    <section
      id="hero-section"
      ref={parallaxRef}
      className={cn(
        "relative overflow-clip lg:px-[5rem] lg:pt-[5rem] ",
        showTopBanner && "mt-[16rem] lg:mt-[7rem]",
        className,
      )}
    >
      <div className="relative overflow-clip w-[min(229.25rem,100%)] h-[108.375rem] lg:h-[94.25rem] mx-auto rounded-[2rem] lg:rounded-[5rem] pt-[13rem] lg:pt-[21.75rem] bg-gradient-to-b from-[#F6F9F7] to-[#E6F2EB]">
        {/* Gradient Fade Overlay */}
        <div className="absolute bottom-0 left-0 z-20 w-full h-[12rem] lg:h-[25rem]">
          <div
            className="absolute bottom-0 left-0 z-10 w-full h-full backdrop-blur-[.12rem] lg:backdrop-blur-[.25rem]"
            style={{
              WebkitMaskImage:
                "linear-gradient(to top, black 75%, transparent 100%)",
              maskImage: "linear-gradient(to top, black 75%, transparent 100%)",
            }}
          />
          <div
            className="absolute bottom-0 left-0 w-full h-[50%] lg:h-[75%] backdrop-blur-[.5rem] lg:backdrop-blur-[1rem] z-10"
            style={{
              WebkitMaskImage:
                "linear-gradient(to top, black 50%, transparent 100%)",
              maskImage: "linear-gradient(to top, black 50%, transparent 100%)",
            }}
          />
          <div
            className="absolute bottom-0 left-0 w-full h-[25%] lg:h-1/2 backdrop-blur-[.35rem] lg:backdrop-blur-[1.25rem] z-10"
            style={{
              WebkitMaskImage:
                "linear-gradient(to top, black 50%, transparent 100%)",
              maskImage: "linear-gradient(to top, black 50%, transparent 100%)",
            }}
          />
          <div
            className={cn(
              "absolute bottom-0 left-0 w-full h-full bg-gradient-to-t to-transparent from-[60%] lg:from-[1%] opacity-30 lg:opacity-100",
              futures ? "from-[#FBFBFB]/90" : "from-[#E6F2EB]/90",
            )}
          />
        </div>

        <div
          className={cn(
            "absolute top-0 left-0 w-full h-full bg-gradient-to-b lg:block hidden",
            futures
              ? "from-[#FBFBFB] -from-[34%] to-[#E6E7F2]"
              : "from-[#F6F9F7] to-[#E6F2EB]",
          )}
        >
          <Image
            className="absolute left-1/2 -translate-x-1/2 bottom-0 !w-[max(100%,90rem)] !max-w-none !h-full object-cover object-top mix-blend-luminosity opacity-[.36]"
            src={bg}
            quality={100}
            alt="background art"
          />
        </div>

        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{
            duration: 0.6,
            delay: 0.1,
            ease: easeInOut,
          }}
          className="absolute z-10 w-[min(52.5rem,100%)] lg:w-[min(112.625rem,100%)] left-[-32rem] bottom-[-9rem] hidden lg:block"
        >
          <Image
            className="!w-full scale-x-[-1]"
            src={coinGuy}
            quality={100}
            alt="statue"
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{
            duration: 0.6,
            delay: 0.3,
            ease: easeInOut,
          }}
          className="absolute z-10 w-[min(35.5rem,100%)] lg:w-[min(59rem,100%)] left-[15%] lg:left-auto lg:right-[-4rem] bottom-[-3rem] lg:bottom-0"
        >
          <Image
            className="!w-full"
            src={statueReading}
            quality={100}
            alt="statue"
          />
        </motion.div>

        <div className="container flex relative z-10 flex-col lg:items-center px-[2.25rem]">
          {data?.heroChip && (
            <p className="font-medium font-instrument text-black/70 text-[2.25rem] tracking-[0.025rem] mb-[1.25rem] lg:mb-[2rem]">
              {data.heroChip}
            </p>
          )}

          {data?.heroText && (
            <div className="richtext w-[min(85.625rem,100%)] hidden lg:block richtext-start lg:richtext-center prose-strong:text-black prose-strong:font-[400]">
              <PortableText
                value={data.heroText}
                components={richTextComponent}
              />
            </div>
          )}

          {data?.heroTextMobile && (
            <div className="w-full lg:hidden richtext richtext-start lg:richtext-center lg:[&_span]:font-display lg:[&_span]:font-[700] [&_span]:font-instrument-sans [&_span]:font-[600] [&_span]:text-[#103037] lg:[&_span]:text-[#65A38A] prose-h1:text-[5rem] prose-h1:mb-[1.5rem] prose-p:text-[2.25rem] prose-p:leading-[150%] prose-p:space-y-[4rem] prose-strong:text-black prose-strong:font-[400]">
              <PortableText
                value={data.heroTextMobile}
                components={richTextComponent}
              />
            </div>
          )}

          {data?.heroCTA && (
            <CTAButton className="mt-[3rem]" data={data.heroCTA} />
          )}
        </div>
      </div>
    </section>
  );
}
